/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { bool, func, shape } from 'prop-types';
import { useParams } from 'react-router-dom';
import * as R from 'ramda';
import { format, isValid } from 'date-fns';
import { GT_ZERO } from 'utils/helpers/validationMessages';

import {
  useGetBatchesFiltersQuery,
  useGetDistributionsFiltersQuery,
  useGetShipmentsFiltersQuery,
} from './batchesEndpoints';

const iconStyles = { position: 'absolute', right: 12, top: 14 };

const Filters = ({
  filters,
  initialState,
  isFiltersOpen,
  pagination,
  setFilters,
  setIsFiltersOpen,
  setPagination,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { view } = useParams();
  const startDate = R.prop('startDate', filters);
  const endDate = R.prop('endDate', filters);

  const [validation, setValidation] = useState('Will return volumes greater or equal to input');
  const [closedIsSelected, setClosedIsSelected] = useState(false);

  // Select different props from filters
  const filtersProp = R.prop(R.__, filters);

  const params = {
    endDate: format(R.prop('endDate', filters), 'MM-dd-yyyy'),
    startDate: format(R.prop('startDate', filters), 'MM-dd-yyyy'),
    status: R.prop('status', filters),
  };

  const { data: batchesData = [], isFetching: isFetchingBatches } = useGetBatchesFiltersQuery(
    {
      params,
    },
    {
      skip: R.or(
        R.not(isFiltersOpen),
        R.not(R.or(R.equals('batch', view), R.equals('shipment', view)))
      ),
    }
  );

  const { data: shipmentsData = [], isFetching: isFetchingShipments } = useGetShipmentsFiltersQuery(
    {
      params,
    },
    {
      skip: R.or(R.not(isFiltersOpen), R.equals('batch', view)),
    }
  );

  const { data: distributionsData = [], isFetching: isFetchingDistributions } =
    useGetDistributionsFiltersQuery(
      {
        params: { ...params, status: params.status === 'CLOSED' ? 'ALL' : params.status },
      },
      {
        skip: R.or(R.not(isFiltersOpen), R.not(R.equals('distribution', view))),
      }
    );

  const isFetching = R.any(R.equals(true))([
    isFetchingBatches,
    isFetchingShipments,
    isFetchingDistributions,
  ]);

  const products = R.cond([
    [R.equals('batch'), R.always(R.prop('products', batchesData))],
    [R.equals('shipment'), R.always(R.prop('products', shipmentsData))],
    [R.equals('distribution'), R.always(R.prop('products', distributionsData))],
  ])(view);

  const origins = R.cond([
    [R.equals('batch'), R.always(R.prop('origins', batchesData))],
    [R.equals('shipment'), R.always(R.prop('origins', shipmentsData))],
  ])(view);

  const locations = R.prop('locations', distributionsData);

  const renderInput = R.curry(({ label, options }, inputParams) => (
    <TextField
      {...inputParams}
      label={label}
      slotProps={{
        input: {
          ...inputParams.InputProps,
          endAdornment: isFetching ? (
            <CircularProgress size={20} sx={{ color: 'secondary.main', ...iconStyles }} />
          ) : !options.length ? (
            <Tooltip title="No options available">
              <InfoRoundedIcon sx={{ color: 'graphic.main', ...iconStyles }} />
            </Tooltip>
          ) : (
            inputParams.InputProps.endAdornment
          ),
        },

        htmlInput: { ...inputParams.inputProps },
      }}
    />
  ));
  const reset = () => setFilters({ ...initialState });

  const resetPage = () => setPagination(R.assoc('page', 0, pagination));

  const handleStartDateChange = date => {
    if (isValid(date)) {
      setFilters(R.assoc('startDate', date, filters));
      resetPage();
    }
  };

  const handleEndDateChange = date => {
    if (isValid(date)) {
      setFilters(R.assoc('endDate', date, filters));
      resetPage();
    }
  };

  useEffect(() => {
    if (R.equals('distribution', view)) {
      if (R.equals('CLOSED', R.prop('status', filters))) {
        setClosedIsSelected(true);
        setFilters(R.assoc('status', 'ALL', filters));
        resetPage();
      }
    } else if (closedIsSelected) {
      if (R.equals('ALL', R.prop('status', filters))) {
        setFilters(R.assoc('status', 'CLOSED', filters));
        resetPage();
      }
      setClosedIsSelected(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // validateVolume :: Number -> String
  const validateVolume = R.cond([
    [R.gte(0), R.always(GT_ZERO)],
    [R.T, R.always('Will return volumes greater or equal to input')],
  ]);

  // Modify handleVolumeChange for safer parsing
  const handleVolumeChange = vol => {
    // Parse and handle invalid values (NaN) by using empty string
    const volume = vol === '' || Number.isNaN(Number(vol)) ? '' : Number(vol);
    setFilters(R.assoc('volume', volume, filters));
    resetPage();
  };

  const safeAutocompleteValue = key => {
    const value = filtersProp(key);
    // Handle both undefined and null values consistently
    return value ?? null;
  };

  return (
    <Drawer
      anchor={lgUp ? 'left' : 'right'}
      onClose={() => setIsFiltersOpen(false)}
      open={isFiltersOpen}
      slotProps={{
        paper: {
          sx: {
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            borderBottomLeftRadius: { xs: 5, lg: 0 },
            borderBottomRightRadius: { xs: 0, lg: 5 },
            borderTopLeftRadius: { xs: 5, lg: 0 },
            borderTopRightRadius: { xs: 0, lg: 5 },
            overflowX: 'hidden',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            width: { xs: '100%', lg: 380 },
          },
        },
      }}
    >
      <Stack
        sx={{
          pr: 2,
          pt: 2,
        }}
      >
        <ClearRoundedIcon
          onClick={() => setIsFiltersOpen(false)}
          sx={{ alignSelf: 'flex-end', color: 'graphic.icons', cursor: 'pointer' }}
        />
      </Stack>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', pl: 3, pr: 1, py: 2 }}
      >
        <Typography color="text.primary" variant="h6">
          Filters
        </Typography>
        <Button
          onClick={() => {
            reset();
            setClosedIsSelected(false);
          }}
          variant="text"
        >
          Clear Filters
        </Button>
      </Stack>
      <Stack spacing={2} sx={{ pb: 3, px: 3 }}>
        <ToggleButtonGroup
          exclusive
          onChange={(_, value) => {
            if (!value) return;
            if (R.equals('distribution', view)) {
              setClosedIsSelected(false);
            }
            R.compose(resetPage, setFilters, R.assoc('status'), R.always(value))();
          }}
          sx={{ height: 40 }}
          value={R.prop('status', filters)}
        >
          <ToggleButton value="ALL">ALL</ToggleButton>
          <ToggleButton value="OPEN">OPEN</ToggleButton>
          <ToggleButton value="ERROR">ERROR</ToggleButton>
          {R.not(R.equals('distribution', view)) && (
            <ToggleButton value="CLOSED">CLOSED</ToggleButton>
          )}
        </ToggleButtonGroup>
        <DatePicker
          label="Start Date"
          maxDate={R.prop('endDate', filters)}
          onChange={handleStartDateChange}
          value={startDate}
        />
        <DatePicker
          label="End Date"
          minDate={R.prop('startDate', filters)}
          onChange={handleEndDateChange}
          value={endDate}
        />
        <Divider />
        {R.equals('batch', view) && (
          <>
            <Autocomplete
              getOptionLabel={option => option.toString()}
              onChange={R.compose(resetPage, setFilters, R.assoc('batchId'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('batchIds', batchesData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Batch Id',
                options: R.defaultTo([], R.prop('batchIds', batchesData)),
              })}
              value={safeAutocompleteValue('batchId')}
            />
            <Autocomplete
              getOptionLabel={option =>
                `${R.prop('batchOwnerIdName', option)} ${R.prop('batchOwnerShortName', option)}`
              }
              isOptionEqualToValue={R.eqProps('batchOwnerIdName')}
              onChange={R.compose(resetPage, setFilters, R.assoc('batchOwner'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('batchOwners', batchesData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Batch Owner',
                options: R.defaultTo([], R.prop('batchOwners', batchesData)),
              })}
              value={safeAutocompleteValue('batchOwner')}
            />
            <Autocomplete
              getOptionLabel={option =>
                R.defaultTo(
                  '',
                  `${R.prop('originOwnerIdName', option)} ${R.prop('originOwnerShortName', option)}`
                )
              }
              isOptionEqualToValue={R.eqProps('originOwnerIdName')}
              onChange={R.compose(resetPage, setFilters, R.assoc('originOwner'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('originOwners', batchesData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Origin Owner',
                options: R.defaultTo([], R.prop('originOwners', batchesData)),
              })}
              value={safeAutocompleteValue('originOwner')}
            />
          </>
        )}
        {R.equals('shipment', view) && (
          <>
            <Autocomplete
              getOptionLabel={option => option.toString()}
              onChange={R.compose(resetPage, setFilters, R.assoc('batchId'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('batchIds', shipmentsData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Batch Id',
                options: R.defaultTo([], R.prop('batchIds', shipmentsData)),
              })}
              value={safeAutocompleteValue('batchId')}
            />

            <Autocomplete
              getOptionLabel={option => option.toString()}
              onChange={R.compose(resetPage, setFilters, R.assoc('shipmentId'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('shipmentIds', shipmentsData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Shipment Id',
                options: R.defaultTo([], R.prop('shipmentIds', shipmentsData)),
              })}
              value={safeAutocompleteValue('shipmentId')}
            />
            <Autocomplete
              getOptionLabel={option =>
                R.defaultTo(
                  '',
                  `${R.prop('shipperIdName', option)} ${R.prop('shipperShortName', option)}`
                )
              }
              isOptionEqualToValue={R.eqProps('shipperIdName')}
              onChange={R.compose(resetPage, setFilters, R.assoc('shipper'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('shippers', shipmentsData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Shipper',
                options: R.defaultTo([], R.prop('shippers', shipmentsData)),
              })}
              value={safeAutocompleteValue('shipper')}
            />
            <Autocomplete
              getOptionLabel={option =>
                R.defaultTo(
                  '',
                  `${R.prop('batchOwnerIdName', option)} ${R.prop('batchOwnerShortName', option)}`
                )
              }
              isOptionEqualToValue={R.eqProps('batchOwnerIdName')}
              onChange={R.compose(resetPage, setFilters, R.assoc('batchOwner'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('batchOwners', shipmentsData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Batch Owner',
                options: R.defaultTo([], R.prop('batchOwners', shipmentsData)),
              })}
              value={safeAutocompleteValue('batchOwner')}
            />
          </>
        )}
        {R.equals('distribution', view) && (
          <>
            <Autocomplete
              getOptionLabel={option => option.toString()}
              onChange={R.compose(resetPage, setFilters, R.assoc('parentShipmentId'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('shipmentRequestIds', distributionsData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Shipment Id',
                options: R.defaultTo([], R.prop('shipmentRequestIds', distributionsData)),
              })}
              value={safeAutocompleteValue('parentShipmentId')}
            />
            <Autocomplete
              getOptionLabel={option => option.toString()}
              onChange={R.compose(resetPage, setFilters, R.assoc('distributionId'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('distributionIds', distributionsData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Distribution Id',
                options: R.defaultTo([], R.prop('distributionIds', distributionsData)),
              })}
              value={safeAutocompleteValue('distributionId')}
            />
            <Autocomplete
              getOptionLabel={option =>
                R.defaultTo(
                  '',
                  `${R.prop('inventoryOwnerId', option)} ${R.prop(
                    'inventoryOwnerShortName',
                    option
                  )}`
                )
              }
              isOptionEqualToValue={R.eqProps('inventoryOwnerId')}
              onChange={R.compose(resetPage, setFilters, R.assoc('inventoryOwner'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('inventoryOwners', distributionsData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Inventory Owner',
                options: R.defaultTo([], R.prop('inventoryOwners', distributionsData)),
              })}
              value={safeAutocompleteValue('inventoryOwner')}
            />
            <Autocomplete
              getOptionLabel={option =>
                R.defaultTo(
                  '',
                  `${R.prop('shipperIdName', option)} ${R.prop('shipperShortName', option)}`
                )
              }
              isOptionEqualToValue={R.eqProps('shipperIdName')}
              onChange={R.compose(resetPage, setFilters, R.assoc('shipper'), R.nthArg(1))}
              options={R.defaultTo([], R.prop('shippers', distributionsData))}
              popupIcon={null}
              renderInput={renderInput({
                label: 'Shipper',
                options: R.defaultTo([], R.prop('shippers', distributionsData)),
              })}
              value={safeAutocompleteValue('shipper')}
            />
          </>
        )}
        <Divider />
        <Autocomplete
          getOptionLabel={R.prop('productName')}
          isOptionEqualToValue={R.eqProps('productId')}
          onChange={R.compose(resetPage, setFilters, R.assoc('product'), R.nthArg(1))}
          options={R.defaultTo([], products)}
          popupIcon={null}
          renderInput={renderInput({
            label: 'Product',
            options: R.defaultTo([], products),
          })}
          value={safeAutocompleteValue('product')}
        />
        {R.equals('distribution', view) ? (
          <Autocomplete
            getOptionLabel={R.prop('locationName')}
            isOptionEqualToValue={R.eqProps('locationId')}
            onChange={R.compose(resetPage, setFilters, R.assoc('location'), R.nthArg(1))}
            options={R.defaultTo([], locations)}
            popupIcon={null}
            renderInput={renderInput({
              label: 'Location',
              options: R.defaultTo([], locations),
            })}
            value={safeAutocompleteValue('location')}
          />
        ) : (
          <Autocomplete
            getOptionLabel={R.prop('originName')}
            isOptionEqualToValue={R.eqProps('originId')}
            onChange={R.compose(resetPage, setFilters, R.assoc('origin'), R.nthArg(1))}
            options={R.defaultTo([], origins)}
            popupIcon={null}
            renderInput={renderInput({
              label: 'Origin',
              options: R.defaultTo([], origins),
            })}
            value={safeAutocompleteValue('origin')}
          />
        )}
        <TextField
          helperText={validation}
          label="Volume"
          onChange={R.compose(
            handleVolumeChange,
            R.tap(R.compose(setValidation, validateVolume)),
            parseInt,
            R.path(['target', 'value'])
          )}
          value={filtersProp('volume') || ''}
        />
      </Stack>
    </Drawer>
  );
};

Filters.propTypes = {
  filters: shape({}).isRequired,
  initialState: shape({}).isRequired,
  isFiltersOpen: bool.isRequired,
  pagination: shape({}).isRequired,
  setFilters: func.isRequired,
  setIsFiltersOpen: func.isRequired,
  setPagination: func.isRequired,
};

export default Filters;
